import { useState, useEffect } from "react";
import { MessageCircle, X } from "lucide-react";
import { trackEvent } from "@/utils/tracking";
import WhatsAppBrandIcon from "@/components/WhatsAppBrandIcon";

const WA_PHONE = "40753094642"; // Service Auto Mobil — WhatsApp

export default function WhatsAppFloat({ t }) {
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(true);
  const w = t?.whatsappFloat;
  const msg = encodeURIComponent(w?.presetMessage || "");
  const href = `https://wa.me/${WA_PHONE}?text=${msg}`;

  useEffect(() => {
    const timer = setTimeout(() => setPulse(false), 6000);
    return () => clearTimeout(timer);
  }, []);

  if (!w) return null;

  return (
    <div
      data-testid="whatsapp-float"
      className="fixed bottom-5 left-5 z-[60] flex flex-col items-start gap-3"
    >
      {open && (
        <div
          data-testid="whatsapp-popup"
          className="bg-white border border-[#e2e6ec] shadow-2xl p-4 max-w-xs animate-[fadeIn_0.2s_ease-out]"
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#25D366]">
                {w.popupEyebrow}
              </div>
              <div className="font-bold text-[#0b1220] text-sm mt-1">{w.title}</div>
            </div>
            <button
              data-testid="whatsapp-close"
              onClick={() => setOpen(false)}
              className="text-slate-400 hover:text-slate-700 transition-colors -mt-1 -mr-1 p-1"
              aria-label={w.closeAria}
              type="button"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-slate-600 leading-relaxed">{w.body}</p>
          <a
            data-testid="whatsapp-cta"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              trackEvent("whatsapp_click", {
                channel: "whatsapp_float",
                phone: WA_PHONE,
              })
            }
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1da851] text-white font-bold uppercase tracking-wider text-xs px-4 py-2.5 transition-colors"
          >
            <WhatsAppBrandIcon className="w-4 h-4" />
            +40 753 094 642
          </a>
        </div>
      )}

      <button
        data-testid="whatsapp-toggle"
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={w.toggleAria}
        className="relative group"
      >
        {pulse && !open && (
          <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-75" />
        )}
        <span className="relative flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1da851] shadow-2xl transition-all group-hover:scale-105">
          {open ? (
            <X className="w-6 h-6 text-white" strokeWidth={2.5} />
          ) : (
            <WhatsAppBrandIcon className="w-7 h-7 text-white" />
          )}
        </span>
      </button>

      {!open && pulse && (
        <span
          className="bg-[#0b1220] text-white font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 shadow-lg pointer-events-none -mt-1 ml-1"
          aria-hidden
        >
          {w.openChip}
        </span>
      )}
    </div>
  );
}
